const ASSETS_URL_KEYS = ['assets_url', 'cdn_url', 'static_url']

const normalizeBaseUrl = (url: string): string => {
    if (!url) return ''
    const sanitized = url.replace(/\/\.\//g, '/')
    return sanitized.endsWith('/') ? sanitized.slice(0, -1) : sanitized
}

const parseExtParams = (rawExtParams: string): Record<string, string> => {
    if (!rawExtParams) return {}

    try {
        const decoded = decodeURIComponent(rawExtParams)
        const parsedJson = JSON.parse(decoded)
        if (parsedJson && typeof parsedJson === 'object') {
            return parsedJson
        }
    } catch (error) {
        // ignored: ext_params might not be JSON
    }

    try {
        const decoded = decodeURIComponent(rawExtParams)
        const params = new URLSearchParams(decoded)
        const result: Record<string, string> = {}

        for (const [key, value] of params.entries()) {
            result[key] = value
        }

        return result
    } catch (error) {
        return {}
    }
}

/**
 * Prefix every asset hangs off when no CDN override is configured.
 *
 * The game ships in two launch shapes and this has to be right in both. Through game_launcher.php
 * the document is `/game_launcher.php?...` at the domain root, with the built index.html inlined
 * into it and a <base> tag pointing at `/games/slots/<game>/<version>/`; window.location and the
 * assets are then in two different places. On a dedicated subdomain the game is served at the root
 * with no <base> at all, and the assets sit next to it.
 *
 * document.baseURI covers both: it follows the <base> tag when there is one and falls back to the
 * document URL when there is not.
 */
const getDocumentBasePrefix = (): string => {
    if (typeof document === 'undefined') return ''

    try {
        const base = new URL(document.baseURI)
        // Launch parameters say nothing about where the assets live, and a value containing a slash
        // would otherwise be mistaken for part of the path.
        base.search = ''
        base.hash = ''

        // Keep the directory, drop the document file name and the trailing slash.
        return base.href.replace(/[^/]*$/, '').replace(/\/$/, '')
    } catch {
        return ''
    }
}

const isAbsoluteUrl = (url: string): boolean => /^(https?:)?\/\//i.test(url) || /^(data|blob):/i.test(url)

/**
 * Where the deployment itself lives: index.html, config.json, the JS bundle.
 *
 * Kept apart from the assets base because in production the two are not the same host. The static
 * files are copied to a CDN while the page keeps being served by the game server, and per-country
 * deployments point at different CDN domains.
 */
export const getDeploymentBaseUrl = (): string => getDocumentBasePrefix()

/**
 * Assets base handed over by the PHP page that renders the game.
 *
 * The launcher already knows which CDN a given country is served from, and it is the only party
 * that does; reading it here avoids hardcoding a domain that changes per market.
 */
const getInjectedAssetsBase = (): string => {
    if (typeof window === 'undefined') return ''

    return normalizeBaseUrl(String((window as { __GAME_ASSETS_BASE__?: string }).__GAME_ASSETS_BASE__ || '').trim())
}

export const getAssetsBaseUrl = (): string => {
    const searchParams = new URLSearchParams(window.location.search)

    for (const key of ASSETS_URL_KEYS) {
        const directValue = searchParams.get(key)
        if (directValue) return normalizeBaseUrl(directValue)
    }

    const extParamsRaw = searchParams.get('ext_params') || ''
    const extParams = parseExtParams(extParamsRaw)
    for (const key of ASSETS_URL_KEYS) {
        if (extParams[key]) return normalizeBaseUrl(extParams[key])
    }

    // After the launch parameters, which stay the per-launch override used for testing, and before
    // the build-time value, which knows nothing about the market being served.
    const injectedBase = getInjectedAssetsBase()
    if (injectedBase) {
        return injectedBase
    }

    // Build-time escape hatch. It is set to "." so the framework packages, which concatenate it
    // directly, stay relative to the deployment; that value carries no location of its own, so it
    // must not be mistaken for a configured base here.
    const buildTimeBase = normalizeBaseUrl(process.env.ASSETS_URL || '')
    if (buildTimeBase && buildTimeBase !== '.') {
        return buildTimeBase
    }

    return ''
}

/**
 * True for the paths that are ours to rewrite. Anything already absolute (http, protocol-relative,
 * data/blob) belongs to the operator or is inlined, and must be left untouched.
 */
const isRewritableAssetPath = (value: string): boolean => /^\/assets\//i.test(value)

/**
 * Rewrites every `/assets/...` string found inside a nested structure.
 *
 * The paytable, the rules and the tournament badge config are plain data with image paths buried at
 * different depths, so resolving them at the boundary where they are handed to the UI keeps a single
 * rule for asset paths instead of a second one spread across the content files.
 */
export const resolveAssetUrls = <T>(value: T): T => {
    if (typeof value === 'string') {
        return (isRewritableAssetPath(value) ? buildAssetUrl(value) : value) as T
    }

    if (Array.isArray(value)) {
        return value.map((item) => resolveAssetUrls(item)) as T
    }

    if (value && typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value as Record<string, unknown>).map(([key, item]) => [key, resolveAssetUrls(item)])
        ) as T
    }

    return value
}

/**
 * The absolute prefix asset URLs are built on.
 *
 * Resolution is done here rather than left to the consumer because several of them anchor relative
 * URLs on the wrong thing: the framework resolves the operator config script against
 * window.location (the launcher, one directory up), and a web worker resolves against its own
 * script URL. An absolute prefix is immune to all of that.
 */
export const getResolvedAssetsBase = (): string => {
    const configuredBase = getAssetsBaseUrl()

    if (!configuredBase) {
        return getDocumentBasePrefix()
    }

    if (isAbsoluteUrl(configuredBase)) {
        return configuredBase
    }

    // A relative override is still relative to the deployed game, not to the launcher.
    const documentBase = getDocumentBasePrefix()
    const relativeBase = configuredBase.replace(/^\.?\//, '')
    return documentBase ? `${documentBase}/${relativeBase}` : configuredBase
}

export const buildAssetUrl = (assetPath: string): string => {
    const normalizedPath = (assetPath.startsWith('/') ? assetPath : `/${assetPath}`).replace(/\/\.\//g, '/')
    const baseUrl = getResolvedAssetsBase()

    if (!baseUrl) {
        return `.${normalizedPath}`
    }

    const baseIncludesAssetsRoot = /\/assets$/i.test(baseUrl)
    const pathStartsWithAssetsRoot = /^\/assets\//i.test(normalizedPath)
    const pathWithoutDuplicatedAssetsRoot =
        baseIncludesAssetsRoot && pathStartsWithAssetsRoot ? normalizedPath.replace(/^\/assets/i, '') : normalizedPath

    // Concatenated rather than run through `new URL`, which would percent-encode the braces in the
    // spine atlas template (`animations_{v}_{n1}.json`) and break Pixi's placeholder substitution.
    return `${baseUrl}${pathWithoutDuplicatedAssetsRoot}`
}
